"use client";
import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import { useTranslations } from "next-intl";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const techItems = [
  ["JavaScript", "TypeScript", "Swift", "PHP"],
  ["PostgreSQL", "MongoDB", "Redis", "MySQL"],
  ["React", "Next.js", "Tailwind CSS", "Framer Motion"],
  ["Node.js", "Laravel", "REST API", "GraphQL"],
  ["React Native", "Swift", "Capacitor"],
  ["AWS", "Google Cloud", "DigitalOcean", "Vercel", "Firebase", "Northflank", "VPS", "Custom Web Servers"],
  ["OAuth2", "JWT", "Auth0", "HTTPS / SSL", "Rate Limiting", "Code Audits"],
  ["Git / GitHub", "Jira", "ClickUp", "Figma", "Jest", "Cypress", "Postman", "Scalar"],
];

const ServicesTech = () => {
  const t = useTranslations("services");
  const techCategories = techItems.map((items, i) => ({
    label: t(`tech.categories.${i}.label`),
    items,
  }));
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <section ref={sectionRef} className="section pb-[60px] lg:pb-[140px] relative">
      <div
        className="absolute left-[-5%] top-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.08)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
        />

      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div className="text-center mb-[50px] lg:mb-[70px]" variants={itemVariants}>
            <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
              {t("tech.sectionTitle")}
            </h2>
            <p className="text-[15px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
              {t("tech.sectionDescription")}
            </p>
          </motion.div>

          <motion.div variants={itemVariants}>
            <AnimatedCard
              className="rounded-[22px] md:rounded-[26px] lg:rounded-[28px]"
              circleClassName="w-[200px] h-[200px] blur-[100px]"
            >
              <BorderWrapper
                wrapperClass="rounded-[22px] md:rounded-[26px] lg:rounded-[28px]"
                className="rounded-[22px] md:rounded-[26px] lg:rounded-[28px] bg-gradient-to-b from-[#121124] to-[#0e0821] relative overflow-hidden"
              >
                <div
                  className="absolute top-0 right-[25%] w-[400px] h-[200px] bg-theme/[0.05] rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                  />

                <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/[0.06]">
                  {/* First row: 4 columns */}
                  {techCategories.slice(0, 4).map((cat, i) => (
                    <div key={i} className="p-[24px] md:p-[26px] lg:p-[30px]">
                      <div className="text-[10px] font-semibold text-theme/60 uppercase tracking-[0.2em] mb-[16px]">
                        {cat.label}
                      </div>
                      <div className="flex flex-wrap gap-[6px]">
                        {cat.items.map((item) => (
                          <span
                            key={item}
                            className="px-[11px] py-[5px] rounded-full text-[12px] font-light border border-white/10 bg-white/[0.03] text-foreground/90 hover:border-theme/30 hover:text-foreground/90 transition-colors duration-200 cursor-default"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Divider row */}
                <div className="border-t border-white/[0.06]" />

                <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/[0.06]">
                  {/* Second row: 4 columns */}
                  {techCategories.slice(4).map((cat, i) => (
                    <div key={i} className="p-[24px] md:p-[26px] lg:p-[30px]">
                      <div className="text-[10px] font-semibold text-theme/60 uppercase tracking-[0.2em] mb-[16px]">
                        {cat.label}
                      </div>
                      <div className="flex flex-wrap gap-[6px]">
                        {cat.items.map((item) => (
                          <span
                            key={item}
                            className="px-[11px] py-[5px] rounded-full text-[12px] font-light border border-white/10 bg-white/[0.03] text-foreground/90 hover:border-theme/30 hover:text-foreground/90 transition-colors duration-200 cursor-default"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </BorderWrapper>
            </AnimatedCard>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesTech;
